import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/ApiService';
import { excelToolExecutor } from '../../services/excel/ExcelToolExecutor';

/** Pane toggle of domain #36: tick the sheets the agent must never write to. Every write targeting a
 *  ticked sheet is confirmed in every mode. Stored in the workbook map (same field as the chat path). */
export function ProtectedSheetsDialog({ workbookId, onClose }: { workbookId: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [sheets, setSheets] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const overview = (await excelToolExecutor.excel_get_workbook_overview()) as { sheets?: { name: string }[] };
        const names = (overview.sheets ?? []).map((s) => s.name);
        const current = workbookId ? await apiService.getProtectedSheets(workbookId).catch(() => []) : [];
        if (cancelled) return;
        setSheets(names);
        setSelected(new Set(current));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workbookId]);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  const save = async () => {
    if (!workbookId) return;
    setSaving(true);
    try {
      await apiService.setProtectedSheets(workbookId, [...selected]);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-b border-line-soft bg-pane px-3.5 py-2 text-[12px]">
      <p className="mb-1 font-medium text-ink">{t('protect.title')}</p>
      <p className="mb-1.5 text-muted">{t('protect.help')}</p>
      {!workbookId && <p className="text-muted">{t('protect.noWorkbook')}</p>}
      {loading ? (
        <p className="text-muted">{t('app.loading')}</p>
      ) : (
        <div className="flex max-h-40 flex-col gap-1 overflow-y-auto">
          {sheets.map((name) => (
            <label key={name} className="flex cursor-pointer items-center gap-2 text-ink">
              <input type="checkbox" checked={selected.has(name)} onChange={() => toggle(name)} />
              <span className="truncate">{name}</span>
            </label>
          ))}
        </div>
      )}
      {error && <p className="mt-1 text-danger">{error}</p>}
      <div className="mt-2 flex items-center gap-2">
        <button type="button" className="rounded-[4px] bg-accent px-2 py-1 text-white disabled:opacity-50" disabled={!workbookId || saving || loading} onClick={() => void save()}>{t('protect.save')}</button>
        <button type="button" className="text-muted" onClick={onClose}>{t('protect.cancel')}</button>
      </div>
    </div>
  );
}
